import IndexLayout from '../layouts';
import Wrapper from '../components/Wrapper';
import SiteNav from '../components/header/SiteNav';
import { SiteHeader, outer, inner, SiteMain } from '../styles/shared';
import * as React from 'react';
import { css } from '@emotion/core';
import { PostFullHeader, PostFullTitle, NoImage, PostFull } from '../templates/post';
import { PostFullContent } from '../components/PostContent';
import Footer from '../components/Footer';
import Helmet from 'react-helmet';

import BackToTop from '../components/BackToTop';

const PageTemplate = css`
  .site-main {
    background #fff;
    padding-bottom: 4vw;
  }

  .post-content {
    max-width: 1000px;
    margin: 0 auto;
  }
  .post-content .large-key .key {
    font-size: 3rem;
    padding: 15px;
    margin-top: 20px;
    display: inline-block;
    border-radius: 6px;
  }
  .large-key {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    margin-top: 4rem;
  }

  @media(max-width: 400px) {
    .large-key {
      grid-template-columns: 1fr 1fr;
    }
  }
`;

const KBShortcuts2: React.FunctionComponent = () => (
  <IndexLayout>
    <Helmet>
      <title>Keyboard Shortcuts for Adobe Lightroom Classic CC | Giggster Guide</title>
      <meta
        name="description"
        content="An easy Lightroom keyboard shortcut reference to help speed up your retouching workflow."
      />
    </Helmet>
    <Wrapper css={PageTemplate}>
      <header css={[outer, SiteHeader]}>
        <div css={inner}>
          <SiteNav />
        </div>
      </header>
      <main id="site-main" className="site-main" css={[SiteMain, outer]}>
        <article className="post page" css={[PostFull, NoImage]}>
          <PostFullHeader>
            <PostFullTitle>Keyboard Shortcuts for Adobe Lightroom Classic CC ⌨️</PostFullTitle>
          </PostFullHeader>

          <PostFullContent className="post-full-content">
            <div className="post-content">
              <p>
                A list of some of the most common &amp; useful Lightroom keyboard shortcuts. Getting
                to know them is a worthwhile investment and saves a ton of time over the long haul.
              </p>
              <p>
                <span className="show-for-small-only">
                  PC users, replace <span className="key">Opt</span> with{' '}
                  <span className="key">Alt</span>, and replace <span className="key">Cmd</span>{' '}
                  with <span className="key">Ctrl</span>.
                </span>
              </p>

              <h2 className="text-center">Tools</h2>

              <div className="row large-key">
                <div className="small-4 columns text-center">
                  <h4>
                    <span>
                      Spot Removal<span className="show-for-large-up"> Tool</span>
                    </span>
                  </h4>
                  <span className="key">Q</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>
                    <span>White Balance</span>
                  </h4>
                  <span className="key">W</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>
                    <span>Crop</span>
                  </h4>
                  <span className="key">R</span>
                </div>
              </div>

              <div className="row large-key t30">
                <div className="small-6 columns text-center">
                  <h4>
                    <span>Adjustment Brush</span>
                  </h4>
                  <span className="key">K</span>
                </div>

                <div className="small-6 columns text-center">
                  <h4>
                    <span>Graduated Filter</span>
                  </h4>
                  <span className="key">M</span>
                </div>
              </div>

              <hr />

              <h2 className="text-center">Single key commands</h2>

              <div className="row large-key t30">
                <div className="small-4 columns text-center">
                  <h4>Library Compare View</h4>
                  <span className="key">C</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Library Grid View</h4>
                  <span className="key">G</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Library Loupe View</h4>
                  <span className="key">E</span>
                </div>
              </div>

              <div className="row large-key t30">
                <div className="small-4 columns text-center">
                  <h4>Survey View</h4>
                  <span className="key">N</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Develop</h4>
                  <span className="key">D</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Info Overlay</h4>
                  <span className="key">I</span>
                </div>
              </div>

              <div className="row large-key t30">
                <div className="small-4 columns text-center">
                  <h4>Flag</h4>
                  <span className="key">P</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Reject</h4>
                  <span className="key">X</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Toggle Fullscreen</h4>
                  <span className="key">F</span>
                </div>
              </div>

              <div className="row large-key t30">
                <div className="small-4 columns text-center">
                  <h4>Before / After Split</h4>
                  <span className="key">Y</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Lights</h4>
                  <span className="key">L</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Star Rating</h4>
                  <span className="key">1-5</span>
                </div>
              </div>

              <div className="row large-key t30">
                <div className="small-4 columns text-center">
                  <h4>Toggle Zoom</h4>
                  <span className="key">Z</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Toggle Toolbar</h4>
                  <span className="key">T</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Toggle Side Panels</h4>
                  <span className="key">Tab</span>
                </div>
              </div>

              <div className="row large-key t30">
                <div className="small-6 columns text-center">
                  <h4>Decrease Brush Size</h4>
                  <span className="key">[</span>
                </div>

                <div className="small-6 columns text-center">
                  <h4>Increase Brush Size</h4>
                  <span className="key">]</span>
                </div>
              </div>

              <hr />

              <h2 className="text-center">Workflow</h2>

              <div className="row large-key">
                <div className="small-4 columns text-center">
                  <h4>Auto Tone</h4>
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">U</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Auto White Balance</h4>
                  <span className="key">Shift</span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">U</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Edit in Photoshop</h4>
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">E</span>
                </div>
              </div>

              <div className="row large-key t30">
                <div className="small-4 columns text-center">
                  <h4>Rotate Counter Clockwise</h4>
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">[</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Rotate Clockwise</h4>
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">]</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Reveal Shortcuts</h4>
                  <span className="key">Shift</span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">/</span>
                </div>
              </div>

              <div className="row large-key t30">
                <div className="small-4 columns text-center">
                  <h4>Zoom In</h4>
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">+</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Zoom Out</h4>
                  <span className="key">
                    Cmd <span className="key-alternate">(Ctrl)</span>
                  </span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">-</span>
                </div>

                <div className="small-4 columns text-center">
                  <h4>Toggle All Panels</h4>
                  <span className="key">Shift</span>
                  <br />
                  <span className="k-plus">+</span>
                  <br />
                  <span className="key">Tab</span>
                </div>
              </div>
            </div>
          </PostFullContent>
        </article>
      </main>
      <BackToTop />
      <Footer />
    </Wrapper>
  </IndexLayout>
);

export default KBShortcuts2;
